import { forwardRef, useLayoutEffect, useRef, type ComponentRef } from "react";

import { Icon } from "../../atoms/icons";
import { Typography } from "../../atoms/typography";
import { cn, platformProps } from "../../lib";
import { nativeOnly, webRole } from "../../lib/web-role";
import { Box, Pressable } from "../../primitives";
import {
  clampStepperValue,
  getStepperAccessibilityLabel,
  getStepperButtonLabels,
  getStepperCountColor,
  getStepperCountLabel,
  isDecrementDisabled,
  isIncrementDisabled,
  stepperBoxClasses,
  stepperButtonClasses,
  stepperButtonLook,
  stepperCountVariant,
  stepperHitSlop,
  stepperIconColor,
  stepperIconSize,
  stepperValueClasses,
} from "./lib";
import type { StepperProps } from "./types";

type StepperButton = ComponentRef<typeof Pressable>;

export const Stepper = ({
  value,
  onChange,
  min = 0,
  max,
  disabled = false,
  size = "sm",
  className,
  testID,
  accessibilityLabel,
}: StepperProps) => {
  const context = { value, min, max, disabled, accessibilityLabel };
  const labels = getStepperButtonLabels(accessibilityLabel);
  const decrementDisabled = isDecrementDisabled(value, min, disabled);
  const incrementDisabled = isIncrementDisabled(value, max, disabled);

  // A press that reaches a limit disables the focused button, and the browser
  // drops focus to the page. Note which button had focus before the screen
  // updates, and hand focus to the other one once the new value has rendered
  const minusRef = useRef<StepperButton>(null);
  const plusRef = useRef<StepperButton>(null);
  const focusedPress = useRef<{ button: "minus" | "plus"; next: number } | null>(null);

  const press = (button: "minus" | "plus", next: number) => {
    const pressed = (button === "minus" ? minusRef : plusRef).current;
    const active: unknown = typeof document !== "undefined" ? document.activeElement : undefined;
    const hadFocus = pressed != null && active === pressed;
    focusedPress.current = hadFocus ? { button, next } : null;
    onChange(next);
  };

  useLayoutEffect(() => {
    const record = focusedPress.current;
    focusedPress.current = null;
    // Only once the screen took the pressed value
    if (!record || record.next !== value) return;
    const nowDisabled = record.button === "minus" ? decrementDisabled : incrementDisabled;
    const otherDisabled = record.button === "minus" ? incrementDisabled : decrementDisabled;
    if (!nowDisabled || otherDisabled) return;
    const pressed = (record.button === "minus" ? minusRef : plusRef).current;
    const active: unknown = document.activeElement;
    // Don't take focus back from another control
    if (active && active !== document.body && active !== pressed) return;
    (record.button === "minus" ? plusRef : minusRef).current?.focus();
  });

  return (
    <Box
      {...platformProps({ testID, slot: "stepper" })}
      // ARIA gives a role-less div no name, so the label needs a group on web
      accessibilityRole={webRole("group")}
      accessibilityLabel={getStepperAccessibilityLabel(context)}
      // Figma's layout: the buttons against the value frame, with no gap. It
      // hugs on web (a block-level flex box filled its container)
      className={cn("web:w-fit flex flex-row items-center", className)}
    >
      <StepperButtonPart
        ref={minusRef}
        icon="Minus"
        size={size}
        disabled={decrementDisabled}
        onClick={() => press("minus", clampStepperValue(value - 1, min, max))}
        accessibilityLabel={labels.decrease}
      />

      {/* A change is announced politely (iOS has no live regions). On iOS and
          Android the count also carries the group's name, which they don't read */}
      <Typography
        variant={stepperCountVariant[size]}
        color={getStepperCountColor(value, min, disabled)}
        align="center"
        className={cn("select-none tabular-nums", stepperValueClasses[size])}
        accessibilityLabel={nativeOnly(getStepperCountLabel(context))}
        {...platformProps({ liveRegion: "polite" })}
      >
        {value}
      </Typography>

      <StepperButtonPart
        ref={plusRef}
        icon="Plus"
        size={size}
        disabled={incrementDisabled}
        onClick={() => press("plus", clampStepperValue(value + 1, min, max))}
        accessibilityLabel={labels.increase}
      />
    </Box>
  );
};

Stepper.displayName = "Stepper";

const StepperButtonPart = forwardRef<
  StepperButton,
  {
    icon: "Minus" | "Plus";
    size: NonNullable<StepperProps["size"]>;
    disabled: boolean;
    onClick: () => void;
    accessibilityLabel: string;
  }
>(({ icon, size, disabled, onClick, accessibilityLabel }, ref) => (
  <Pressable
    ref={ref}
    accessibilityRole="button"
    accessibilityLabel={accessibilityLabel}
    disabled={disabled}
    onClick={onClick}
    hitSlop={stepperHitSlop[size]}
    className={cn(stepperButtonClasses, stepperButtonLook(disabled), stepperBoxClasses[size])}
  >
    <Icon name={icon} size={stepperIconSize[size]} className={stepperIconColor(disabled)} />
  </Pressable>
));

StepperButtonPart.displayName = "StepperButtonPart";
